import { AloftDate, FormType, ReportJSON } from '@aloft/toolkit-shared';

import { create2DCompsArray } from '../utils.ts';
import { createAutoAdjustmentsSalesCompUadNode } from './forms/autoAdjustmentsSalesCompUad.ts';
import { create1004FormNode } from './forms/fnma1004.ts';
import {
  create1004AdditionalCompsFormNode,
  create2055AdditionalCompsFormNode,
} from './forms/fnma1004AdditionalComps.ts';
import { create1004cFormNode } from './forms/fnma1004c.ts';
import { create1004cAdditionalCompsFormNode } from './forms/fnma1004cAdditionalComps.ts';
import { create1025FormNode } from './forms/fnma1025.ts';
import { create1025AdditionalCompsFormNode } from './forms/fnma1025AdditionalComps.ts';
import { create2055FormNode } from './forms/fnma2055.ts';
import { createPdfAddendumNode } from './forms/pdfAddendum.ts';
import { generateToolkitApplicationRptiNode } from './generateToolkitApplicationRptiNode.ts';

export const generateTkv2SfrepReport = (
  effectiveDate: AloftDate | undefined,
  reportJSON: ReportJSON,
  pdfs: { blob: Blob; name: string }[],
  formType: FormType = FormType.fnma1004,
): string => {
  const doc = document.implementation.createDocument('', '', null);
  const { comps, compSequence, meta } = reportJSON;
  // Create Report Element
  const report = doc.createElement('Report');
  report.setAttribute('xmlns:xsd', 'http://www.w3.org/2001/XMLSchema');
  report.setAttribute('xmlns:xsi', 'http://www.w3.org/2001/XMLSchema-instance');
  doc.appendChild(report);

  report.appendChild(generateToolkitApplicationRptiNode(doc));

  //
  const compSequence2dArray = create2DCompsArray(compSequence);

  // Forms
  const forms = doc.createElement('Forms');
  report.appendChild(forms);

  const majorForm = createMajorFormNode(
    doc,
    effectiveDate,
    compSequence2dArray[0] ?? [],
    reportJSON,
    formType,
  );
  forms.append(majorForm);

  if (compSequence2dArray.length > 1) {
    const additionalForms = createAdditionalCompsForms(
      doc,
      compSequence2dArray,
      comps,
      formType,
      meta,
    );
    forms.append(...additionalForms);
  }

  for (let j = 0; j < pdfs.length; j++) {
    const pdf = pdfs[j];
    if (!pdf) continue;
    const pdfNode = createPdfAddendumNode(doc, pdf.name);
    forms.append(pdfNode);
  }

  // Sales Comp Grid Auto Adjustment Settings
  const autoAdjustmentSettings = createAutoAdjustmentsSalesCompUadNode(doc);
  forms.append(autoAdjustmentSettings);

  const serializer = new XMLSerializer();
  const reportString = serializer.serializeToString(doc);

  return reportString;
};

function createMajorFormNode(
  doc: XMLDocument,
  effectiveDate: AloftDate | undefined,
  compSequence: string[],
  reportJSON: ReportJSON,
  formType: FormType,
): HTMLElement {
  let majorFormNode: HTMLElement;

  switch (formType) {
    case FormType.fnma1004:
      majorFormNode = create1004FormNode(doc, effectiveDate, compSequence, reportJSON);
      break;
    case FormType.fnma2055:
      majorFormNode = create2055FormNode(doc, effectiveDate, compSequence, reportJSON);
      break;
    case FormType.fnma1004c:
      majorFormNode = create1004cFormNode(doc, effectiveDate, compSequence, reportJSON);
      break;
    case FormType.fnma1025:
      majorFormNode = create1025FormNode(doc, effectiveDate, compSequence, reportJSON);
      break;
    default:
      throw new Error(`Form Type ${formType} not yet supported`);
  }

  return majorFormNode;
}

function createAdditionalCompsForms(
  doc: XMLDocument,
  compSequence2dArray: string[][],
  comps: ReportJSON['comps'],
  formType: FormType,
  meta?: ReportJSON['meta'],
): HTMLElement[] {
  const additionalCompsNodes: HTMLElement[] = [];
  let createNodesFnc;

  switch (formType) {
    case FormType.fnma1004:
      createNodesFnc = create1004AdditionalCompsFormNode;
      break;
    case FormType.fnma2055:
      createNodesFnc = create2055AdditionalCompsFormNode;
      break;
    case FormType.fnma1004c:
      createNodesFnc = create1004cAdditionalCompsFormNode;
      break;
    case FormType.fnma1025:
      createNodesFnc = create1025AdditionalCompsFormNode;
      break;
    default:
      throw new Error(`Form Type ${formType} not yet supported`);
  }

  for (let i = 1; i < compSequence2dArray.length; i++) {
    const additionalComps = createNodesFnc(doc, compSequence2dArray[i] ?? [], comps, meta);
    additionalCompsNodes.push(additionalComps);
  }

  return additionalCompsNodes;
}
